<template>
    <card title="AR Statements">
        <div v-if="arConfigLoading" class="text-center p-3 d-flex align-items-center justify-content-center"
            style="gap: 6px;">
            <b-spinner small /> Loading...
        </div>
        <div v-else-if="arConfigUnavailable" class="text-center p-3 text-muted">
            Accounts Receivable settings could not be loaded. Refresh to try again.
        </div>
        <div v-else-if="arDisabled" class="text-center p-3 text-muted">
            Accounts Receivable is not enabled for this pharmacy.
        </div>
        <template v-else>
            <b-row>
                <b-col cols="6">
                    <SearchComponent v-model="patient" label="Patient" search-u-r-l="/Patient" clearable />
                </b-col>
                <b-col v-if="accounts.length > 1" cols="6">
                    <b-form-group label="Account">
                        <b-select v-model="selectedAccountId" :options="accountOptions" @change="loadStatements" />
                    </b-form-group>
                </b-col>
            </b-row>

            <div v-if="accountsLoading" class="text-center p-3 d-flex align-items-center justify-content-center"
                style="gap: 6px;">
                <b-spinner small /> Loading accounts...
            </div>

            <div v-else-if="!patientId" class="text-center p-3 text-muted">
                Search for a patient to see their statements.
            </div>

            <!-- Ahead of the no-account state on purpose: a lookup that failed is not evidence the
                 patient has no account, and the message below sends the user off to create one. -->
            <div v-else-if="accountsLoadFailed" class="text-center p-3 text-muted">
                This patient's AR accounts could not be loaded. Refresh to try again.
            </div>

            <div v-else-if="!accounts.length" class="text-center p-3 text-muted">
                This patient has no AR account. Create one from the Accounts Receivable card on the
                patient's profile.
            </div>

            <template v-else-if="selectedAccount">
                <b-row class="align-items-center mb-2">
                    <b-col cols="6">
                        <strong>{{ selectedAccount.accountNumber }}</strong>
                        <b-badge :variant="accountStatusVariant(selectedAccount.status)" class="ml-1">
                            {{ accountStatusLabel(selectedAccount.status) }}
                        </b-badge>
                    </b-col>
                    <b-col cols="6" class="text-right">
                        <strong>Balance:</strong> {{ currency(selectedAccount.currentBalance) }}
                        <!-- Trailing ellipsis: this opens the modal to collect a period rather
                             than generating anything on the spot. -->
                        <b-button v-if="canGenerateStatement" size="sm" variant="primary" class="ml-3"
                            @click="showGenerateModal = true">
                            Generate Statement...
                        </b-button>
                    </b-col>
                </b-row>

                <!-- Deliberately not `responsive`: that wraps the table in an overflow container
                     which both scrolls the statement list away from the rest of the card and
                     clips the row action menu. The seven columns are short enough to fit. -->
                <b-table :fields="fields" :items="statements" :busy="loading" small show-empty
                    sort-by="statementDate" :sort-desc="true">
                    <template #table-busy>
                        <div class="text-center my-2"><b-spinner small /> Loading...</div>
                    </template>
                    <template #empty>
                        <div class="text-center text-muted">No statements have been generated for this account.</div>
                    </template>
                    <template #cell(status)="row">
                        <b-badge :variant="statementStatusVariant(row.item.status)">
                            {{ statementStatusLabel(row.item.status) }}
                        </b-badge>
                    </template>
                    <template #cell(actions)="row">
                        <!-- Bound to the viewport rather than the b-dropdown default of the
                             nearest scroll parent, so the menu is never clipped by the page's
                             own scrolling container on the last row. -->
                        <b-dropdown size="sm" variant="outline-secondary" text="Actions" right
                            boundary="viewport" :disabled="isRowBusy(row.item.id)">
                            <b-dropdown-item :disabled="!row.item.pdfPath" @click="viewPdf(row.item)">
                                View PDF
                            </b-dropdown-item>
                            <b-dropdown-item :disabled="!row.item.pdfPath" @click="downloadPdf(row.item)">
                                Download PDF
                            </b-dropdown-item>
                            <b-dropdown-item v-if="canGenerateStatement" @click="confirmResend(row.item)">
                                Re-send Statement
                            </b-dropdown-item>
                            <b-dropdown-divider />
                            <b-dropdown-item @click="openDeliveryDetail(row.item)">
                                Delivery Status
                            </b-dropdown-item>
                            <b-dropdown-item @click="openPeriodLedger(row.item)">
                                View Ledger for Period
                            </b-dropdown-item>
                        </b-dropdown>
                    </template>
                </b-table>
            </template>
        </template>
            <ARStatementDeliveryModal v-model="showDeliveryModal" :statement="activeStatement" />
        <ARStatementLedgerModal v-model="showLedgerModal" :statement="activeStatement" />
        <ARGenerateStatementModal v-model="showGenerateModal" :account-id="selectedAccountId"
            :account-number="selectedAccount ? selectedAccount.accountNumber : ''" @saved="loadStatements" />
    </card>
</template>

<script lang="ts">
import { Component, Mixins, Watch } from "vue-property-decorator";
import {
    ARAccountSummary,
    ARResendStatementResult,
    ARStatementSummary,
    arAccountStatusLabel,
    arNoDeliveryChannelReason,
    arAccountStatusVariant,
    arStatementStatusLabel,
    arStatementStatusVariant,
} from "@/models/AR";
import TextUtils from "@/util/TextUtils";
import { Patient } from "@/models/Patient";
import axios from "axios";
import moment from "moment";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import AREnabledMixin from "@/mixins/AREnabledMixin";
import ARPermissionsMixin from "@/mixins/ARPermissionsMixin";
import SearchComponent from "@/components/SearchComponent.vue";
import ARStatementDeliveryModal from "@/components/Patient/ARStatementDeliveryModal.vue";
import ARStatementLedgerModal from "@/components/Patient/ARStatementLedgerModal.vue";
import ARGenerateStatementModal from "@/components/Patient/ARGenerateStatementModal.vue";

@Component({
    name: "ARStatementsPage",
    components: {
        SearchComponent,
        ARStatementDeliveryModal,
        ARStatementLedgerModal,
        ARGenerateStatementModal,
    },
})
export default class ARStatementsPage extends Mixins(AREnabledMixin, ARPermissionsMixin) {
    private loading = false;
    private accountsLoading = false;
    // Kept distinct from an empty account list for the same reason the AR flag is tri-state: an
    // account lookup that failed is not the same as a patient who has no account, and the empty
    // state for the latter tells the user to go create one.
    private accountsLoadFailed = false;
    // Nullable because the patient search is clearable, and v-select emits null on clear, but it
    // has to start as a real Patient: SearchComponent captures the model type once on mount and
    // falls back to rendering raw ids for the life of the component if the bound value was not a
    // HasLabel by then. An empty Patient has no id, so the unsearched state still reads as null.
    private patient: Patient | null = new Patient();
    private accounts: ARAccountSummary[] = [];
    private selectedAccountId: number | null = null;
    private statements: ARStatementSummary[] = [];
    // The statement a row action is acting on. Re-send is per row, so the id is tracked separately
    // to disable only that row's menu rather than every row's.
    private activeStatement: ARStatementSummary | null = null;
    private busyStatementId: number | null = null;
    private showDeliveryModal = false;
    private showLedgerModal = false;
    private showGenerateModal = false;
    // Blob URLs opened in a tab, keyed by their pending revoke timer so teardown can cancel the
    // timer and free the blob immediately.
    private pendingRevokes: Map<number, string> = new Map();
    // Both lookups are driven by controls the user can change again before the previous response
    // lands: the patient search and the account dropdown. Each request captures the counter it was
    // issued under and drops its response once the counter has moved on, so an earlier patient's
    // accounts and statements can never be written in under the patient now on screen — which
    // would leave the row actions and Generate acting on an account nobody is looking at.
    private accountsRequestId = 0;
    private statementsRequestId = 0;

    get patientId(): number | null {
        return this.patient?.id ?? null;
    }

    get selectedAccount(): ARAccountSummary | null {
        return this.accounts.find(a => a.id === this.selectedAccountId) ?? null;
    }

    get accountOptions() {
        return this.accounts.map(a => ({
            value: a.id,
            text: `${a.accountNumber ?? `#${a.id}`} — ${this.currency(a.currentBalance)}`,
        }));
    }

    get fields() {
        const money = (v: number) => this.currency(v);
        const date = (v: string) => this.formatDate(v);
        return [
            { key: "statementDate", label: "Statement Date", sortable: true, formatter: date },
            { key: "periodStart", label: "Period Start", formatter: date },
            { key: "periodEnd", label: "Period End", formatter: date },
            { key: "endingBalance", label: "Ending Balance", formatter: money },
            { key: "dueByDate", label: "Due By", formatter: date },
            { key: "status", label: "Status" },
            { key: "actions", label: "", tdClass: "text-right" },
        ];
    }

    async mounted(): Promise<void> {
        await this.loadArEnabled();
    }

    // Selecting a different patient invalidates the account and everything loaded under it, so the
    // table is cleared before the new accounts arrive rather than showing the previous patient's
    // statements against the new patient's name.
    @Watch("patient")
    async onPatientChanged(): Promise<void> {
        // The busy flags are cleared here, not by the retired requests: a superseded response no
        // longer owns them.
        this.accountsRequestId++;
        this.statementsRequestId++;
        this.accountsLoading = false;
        this.loading = false;
        this.accounts = [];
        this.selectedAccountId = null;
        this.statements = [];
        this.accountsLoadFailed = false;
        if (!this.patientId) return;
        await this.loadAccounts();
    }

    private async loadAccounts(): Promise<void> {
        if (!this.patientId) return;
        const requestId = ++this.accountsRequestId;
        try {
            this.accountsLoading = true;
            this.accountsLoadFailed = false;
            const response = await axios.get<ARAccountSummary[]>(`/AR/patients/${this.patientId}/accounts`);
            if (requestId !== this.accountsRequestId) return;
            this.accounts = response.data ?? [];
            this.selectedAccountId = this.accounts[0]?.id ?? null;
            if (this.selectedAccountId) await this.loadStatements();
        } catch (error) {
            // A superseded request's failure is not the current patient's failure: reporting it
            // would raise the error state over accounts that loaded fine.
            if (requestId !== this.accountsRequestId) return;
            this.$notification(NotificationOptions.error(error));
            this.accounts = [];
            this.selectedAccountId = null;
            this.accountsLoadFailed = true;
        } finally {
            // Only the newest request owns the spinner. Clearing it from a superseded one uncovers
            // "this patient has no AR account" while the real lookup is still out.
            if (requestId === this.accountsRequestId) this.accountsLoading = false;
        }
    }

    async loadStatements(): Promise<void> {
        // Bumped ahead of the account check so that clearing the selection also retires a request
        // already in flight rather than letting it land against a different account.
        const requestId = ++this.statementsRequestId;
        if (!this.selectedAccountId) {
            // Retired the in-flight request without issuing a replacement, so nothing else is left
            // to turn the table spinner off.
            this.loading = false;
            return;
        }
        try {
            this.loading = true;
            const response = await axios.get<ARStatementSummary[]>(
                `/AR/accounts/${this.selectedAccountId}/statements`);
            if (requestId !== this.statementsRequestId) return;
            this.statements = response.data ?? [];
        } catch (error) {
            if (requestId !== this.statementsRequestId) return;
            this.$notification(NotificationOptions.error(error));
            this.statements = [];
        } finally {
            if (requestId === this.statementsRequestId) this.loading = false;
        }
    }

    isRowBusy(statementId: number): boolean {
        return this.busyStatementId === statementId;
    }

    // Re-sending emails or texts the responsible party again, so it confirms first — the action sits
    // in a menu next to read-only options and is easy to hit by mistake.
    async confirmResend(statement: ARStatementSummary): Promise<void> {
        const confirmed = await this.$bvModal.msgBoxConfirm(
            `Re-render and re-send the statement dated ${this.formatDate(statement.statementDate)} ` +
            `through this account's configured delivery channels?`,
            { title: "Re-send Statement", okTitle: "Re-send", okVariant: "primary", cancelTitle: "Cancel" }
        );
        if (confirmed) await this.resend(statement);
    }

    private async resend(statement: ARStatementSummary): Promise<void> {
        try {
            this.busyStatementId = statement.id;
            const response = await axios.post<ARResendStatementResult>(
                `/AR/statements/${statement.id}/resend`);

            // A 200 means re-rendered and archived, not received — see ARResendStatementResult.
            this.$notification(response.data?.delivered
                ? NotificationOptions.success("Statement re-sent.")
                : NotificationOptions.warning(
                    `The statement was re-rendered but no delivery channel succeeded. ` +
                    `${arNoDeliveryChannelReason} See Delivery Status.`));

            // Re-send returns the refreshed row, so only that row's delivery state is replaced.
            // Reloading the whole table would put every row behind the table-level busy spinner,
            // which is what the per-row busy id exists to avoid.
            const index = this.statements.findIndex(s => s.id === statement.id);
            if (index >= 0 && response.data?.statement) {
                this.$set(this.statements, index, response.data.statement);
            }
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.busyStatementId = null;
        }
    }

    async viewPdf(statement: ARStatementSummary): Promise<void> {
        // The PDF needs an authenticated request, so it cannot be opened by URL — it is fetched
        // and opened as a blob. The tab has to be opened synchronously inside the click's user
        // gesture: opening it after the request resolves is outside the gesture, and browsers
        // block that as a popup.
        const tab = window.open("", "_blank");
        const url = await this.fetchPdfObjectUrl(statement);

        if (!url) {
            tab?.close();
            return;
        }

        if (!tab) {
            // Blocked anyway (a blanket popup blocker). Downloading still gets the user the
            // document rather than failing silently.
            this.triggerDownload(url, `statement-${statement.id}.pdf`);
            return;
        }

        tab.location.href = url;
        // The tab needs the URL to still resolve when it loads, so it is revoked on a delay rather
        // than immediately. Without the revoke the blob is held for the life of the page.
        this.pendingRevokes.set(window.setTimeout(() => this.revokeNow(url), 60000), url);
    }

    // Leaving the page before a timer fires would otherwise strand its blob for the rest of the
    // session, so anything still pending is revoked on teardown.
    beforeDestroy(): void {
        this.pendingRevokes.forEach((url, handle) => {
            window.clearTimeout(handle);
            URL.revokeObjectURL(url);
        });
        this.pendingRevokes.clear();
    }

    private revokeNow(url: string): void {
        URL.revokeObjectURL(url);
        this.pendingRevokes.forEach((pending, handle) => {
            if (pending === url) this.pendingRevokes.delete(handle);
        });
    }

    async downloadPdf(statement: ARStatementSummary): Promise<void> {
        const url = await this.fetchPdfObjectUrl(statement);
        if (!url) return;
        this.triggerDownload(url, `statement-${statement.id}.pdf`);
    }

    private triggerDownload(url: string, fileName: string): void {
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    private async fetchPdfObjectUrl(statement: ARStatementSummary): Promise<string | null> {
        try {
            this.busyStatementId = statement.id;
            const response = await axios.get(`/AR/statements/${statement.id}/pdf`, { responseType: "blob" });
            return URL.createObjectURL(response.data);
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
            return null;
        } finally {
            this.busyStatementId = null;
        }
    }

    openDeliveryDetail(statement: ARStatementSummary): void {
        this.activeStatement = statement;
        this.showDeliveryModal = true;
    }

    openPeriodLedger(statement: ARStatementSummary): void {
        this.activeStatement = statement;
        this.showLedgerModal = true;
    }

    statementStatusLabel(status?: number | null): string {
        return arStatementStatusLabel(status);
    }

    statementStatusVariant(status?: number | null): string {
        return arStatementStatusVariant(status);
    }

    accountStatusLabel(status?: number): string {
        return arAccountStatusLabel(status);
    }

    accountStatusVariant(status?: number): string {
        return arAccountStatusVariant(status);
    }

    currency(value?: number | null): string {
        return TextUtils.currencyFormatter(value ?? 0);
    }

    // Statement dates, period bounds and the due date are date-only values. Parsing them as UTC and
    // converting to the viewer's zone rolls midnight back a day everywhere west of UTC, so they are
    // formatted with no conversion — the same convention as dateOfBirth, fillDate and expirationDate.
    formatDate(date?: string | null): string {
        return date ? moment(date).format("L") : "";
    }
}
</script>

<style scoped></style>
